import { useQuery } from "@apollo/client/react";
import { useParams } from "react-router-dom";
import { ReplayHeatmap, RetentionChart, StatCard, StatCardGroup } from "@athena/ui";
import { VideoAnalyticsDocument } from "@/graphql/videoAnalytics.generated";

/**
 * Per-video analytics dashboard (ADR 0005): viewership, watch time, audience
 * retention, most-replayed heatmap, reactions, and (anonymized) feedback for one
 * video lesson. Reached at /analytics/video/:contentId.
 */

const fmtDuration = (secs: number): string => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = Math.floor(secs % 60);
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
};

const pct = (v: number): string => `${Math.round(v * 100)}%`;

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section className="border-border bg-card rounded-lg border p-4">
            <h2 className="text-muted-foreground mb-3 text-sm font-medium">{title}</h2>
            {children}
        </section>
    );
}

export function VideoAnalyticsPage() {
    const { contentId = "" } = useParams();
    const { data, loading, error } = useQuery(VideoAnalyticsDocument, {
        variables: { contentId },
        skip: !contentId,
    });

    const a = data?.videoAnalytics;

    // Keyed on data, not `loading`: Apollo sets loading on every refetch, so gating
    // the whole page on it replaces live content — and unmounts any child holding
    // state or an in-flight request — every time something reloads.
    if (loading && !data) return <div className="text-muted-foreground p-6 text-sm">Loading analytics…</div>;
    if (error) return <div className="text-destructive p-6 text-sm">Failed to load analytics.</div>;
    if (!a) return <div className="text-muted-foreground p-6 text-sm">No analytics for this content.</div>;

    return (
        <div className="space-y-6 p-6">
            <h1 className="text-xl font-semibold">Video analytics</h1>

            <StatCardGroup className="w-full">
                <StatCard label="Views" value={a.views} detail={`${a.uniqueViewers} unique viewers`} />
                <StatCard
                    label="Watch time"
                    value={fmtDuration(a.totalWatchSeconds)}
                    detail={`avg ${fmtDuration(Math.round(a.averageViewSeconds))} / view`}
                />
                <StatCard label="Avg. watched" value={pct(a.averagePercentWatched)} detail="of each video" />
                <StatCard label="Completion" value={pct(a.completionRate)} detail="reached the end" />
                <StatCard
                    label="Reactions"
                    value={`👍 ${a.likes}  👎 ${a.dislikes}`}
                    detail={a.likes + a.dislikes === 0 ? "no reactions yet" : undefined}
                />
                <StatCard
                    label="Rating"
                    value={a.averageRating != null ? `${a.averageRating.toFixed(1)} ★` : "—"}
                    detail={`${a.ratingCount} rating${a.ratingCount === 1 ? "" : "s"}`}
                />
            </StatCardGroup>

            <Panel title="Audience retention">
                {a.retention.length > 0 ? (
                    <RetentionChart points={a.retention} />
                ) : (
                    <p className="text-muted-foreground text-sm">No watch data yet.</p>
                )}
            </Panel>

            <Panel title="Most replayed">
                {a.mostReplayed.length > 0 ? (
                    <ReplayHeatmap points={a.mostReplayed} />
                ) : (
                    <p className="text-muted-foreground text-sm">No watch data yet.</p>
                )}
            </Panel>

            <Panel title={`Feedback (${a.feedback.length})`}>
                {a.feedback.length > 0 ? (
                    <ul className="space-y-3">
                        {a.feedback.map((f) => (
                            <li key={f.id} className="border-border border-b pb-3 last:border-0">
                                <div className="text-sm">
                                    {f.rating != null && (
                                        <span className="text-yellow-500">{"★".repeat(f.rating)}</span>
                                    )}
                                    {f.comment && <span className="ml-2">{f.comment}</span>}
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-muted-foreground text-sm">No feedback yet.</p>
                )}
            </Panel>
        </div>
    );
}
